import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingConfig } from './types/infiniteScroll';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', color = 'currentColor' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <motion.div
      className={`${sizeClasses[size]} rounded-full border-2 border-gray-300 border-t-current`}
      style={{ borderTopColor: color }}
      animate={{ rotate: 360 }}
      transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'linear',
      }}
      role="status"
      aria-label="Loading"
    />
  );
};

interface LoadingDotsProps {
  color?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({ color = 'currentColor', size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-2 h-2',
    lg: 'w-3 h-3',
  };

  const dotVariants = {
    initial: { y: 0, opacity: 0.5 },
    animate: { y: -8, opacity: 1 },
  };

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <motion.div
      className="flex items-center space-x-1"
      variants={containerVariants}
      initial="initial"
      animate="animate"
      role="status"
      aria-label="Loading"
    >
      {[0, 1, 2].map((index) => (
        <motion.div
          key={index}
          className={`${sizeClasses[size]} rounded-full`}
          style={{ backgroundColor: color }}
          variants={dotVariants}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut',
          }}
        />
      ))}
    </motion.div>
  );
};

interface LoadingPulseProps {
  children: React.ReactNode;
  color?: string;
}

export const LoadingPulse: React.FC<LoadingPulseProps> = ({ children, color = '#e5e7eb' }) => {
  return (
    <motion.div
      className="rounded-lg"
      style={{ backgroundColor: color }}
      animate={{
        opacity: [0.5, 1, 0.5],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      role="status"
      aria-label="Loading content"
    >
      {children}
    </motion.div>
  );
};

interface LoadingBarProps {
  progress?: number;
  color?: string;
  height?: number;
}

export const LoadingBar: React.FC<LoadingBarProps> = ({ progress, color = '#3b82f6', height = 4 }) => {
  return (
    <div
      className="w-full overflow-hidden rounded-full bg-gray-200"
      style={{ height }}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <motion.div
        className="h-full rounded-full"
        style={{ backgroundColor: color }}
        initial={{ width: 0 }}
        animate={{
          width: progress !== undefined ? `${progress}%` : '100%',
          x: progress === undefined ? ['-100%', '100%'] : 0,
        }}
        transition={{
          duration: progress !== undefined ? 0.3 : 1.5,
          repeat: progress !== undefined ? 0 : Infinity,
          ease: progress !== undefined ? 'easeOut' : 'easeInOut',
        }}
      />
    </div>
  );
};

interface InfiniteScrollLoadingProps {
  config: LoadingConfig;
  isInitial?: boolean;
  className?: string;
}

export const InfiniteScrollLoading: React.FC<InfiniteScrollLoadingProps> = ({
  config,
  isInitial = false,
  className = '',
}) => {
  const duration = (config.animationDuration || 300) / 1000;

  // If custom component is provided, use it
  if (config.component) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration }}
          className={`flex items-center justify-center ${className}`}
        >
          {config.component}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.9 }}
        transition={{ duration, ease: 'easeOut' }}
        className={`flex flex-col items-center justify-center py-8 ${className}`}
      >
        {config.showSpinner && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="mb-4"
          >
            <LoadingSpinner size={isInitial ? 'lg' : 'md'} />
          </motion.div>
        )}

        {config.text && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="text-sm font-medium text-gray-600"
          >
            {config.text}
          </motion.p>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

interface ErrorStateProps {
  error: string;
  onRetry: () => void;
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, className = '' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`flex flex-col items-center justify-center py-8 ${className}`}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-4 h-12 w-12 text-red-500"
      >
        <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-4 max-w-sm text-center text-gray-700"
      >
        {error}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onRetry}
        className="rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
      >
        Try Again
      </motion.button>
    </motion.div>
  );
};

interface EmptyStateProps {
  message?: string;
  icon?: React.ReactNode;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ message = 'No items found', icon, className = '' }) => {
  const defaultIcon = (
    <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex flex-col items-center justify-center py-12 ${className}`}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
        className="mb-4"
      >
        {icon || defaultIcon}
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center text-lg font-medium text-gray-600"
      >
        {message}
      </motion.p>
    </motion.div>
  );
};

interface SkeletonLoaderProps {
  count?: number;
  height?: number;
  className?: string;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ count = 3, height = 80, className = '' }) => {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="mb-4"
        >
          <LoadingPulse>
            <div className="w-full rounded-lg" style={{ height: `${height}px` }} />
          </LoadingPulse>
        </motion.div>
      ))}
    </div>
  );
};
